/**
 * "/" commands in every editor: headings, lists, quotes, code, a divider
 * and today's date. Typing filters the list; Enter inserts.
 */
import { cn } from "@/lib/utils";
import { Extension, type Editor, type Range } from "@tiptap/core";
import { PluginKey } from "@tiptap/pm/state";
import { ReactRenderer } from "@tiptap/react";
import Suggestion, { type SuggestionOptions, type SuggestionProps } from "@tiptap/suggestion";
import { format } from "date-fns";
import { CalendarDays, CheckSquare, Code2, Heading1, Heading2, Heading3, List, ListOrdered, Minus, TextQuote, type LucideIcon } from "lucide-react";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";

interface SlashItem {
  id: string;
  label: string;
  hint: string;
  icon: LucideIcon;
  keywords?: string;
  run: (editor: Editor, range: Range) => void;
}

const ITEMS: SlashItem[] = [
  { id: "h1", label: "Heading 1", hint: "Big section title", icon: Heading1, keywords: "title", run: (e, r) => e.chain().focus().deleteRange(r).setNode("heading", { level: 1 }).run() },
  { id: "h2", label: "Heading 2", hint: "Section", icon: Heading2, run: (e, r) => e.chain().focus().deleteRange(r).setNode("heading", { level: 2 }).run() },
  { id: "h3", label: "Heading 3", hint: "Subsection", icon: Heading3, run: (e, r) => e.chain().focus().deleteRange(r).setNode("heading", { level: 3 }).run() },
  { id: "bullets", label: "Bullet list", hint: "Plain list", icon: List, keywords: "ul unordered", run: (e, r) => e.chain().focus().deleteRange(r).toggleBulletList().run() },
  { id: "numbers", label: "Numbered list", hint: "Steps in order", icon: ListOrdered, keywords: "ol ordered", run: (e, r) => e.chain().focus().deleteRange(r).toggleOrderedList().run() },
  { id: "tasks", label: "Task list", hint: "Checkboxes", icon: CheckSquare, keywords: "todo checklist", run: (e, r) => e.chain().focus().deleteRange(r).toggleTaskList().run() },
  { id: "quote", label: "Quote", hint: "Pull a line out", icon: TextQuote, keywords: "blockquote", run: (e, r) => e.chain().focus().deleteRange(r).toggleBlockquote().run() },
  { id: "code", label: "Code block", hint: "Monospace, as typed", icon: Code2, keywords: "pre snippet", run: (e, r) => e.chain().focus().deleteRange(r).toggleCodeBlock().run() },
  { id: "divider", label: "Divider", hint: "A horizontal rule", icon: Minus, keywords: "hr line", run: (e, r) => e.chain().focus().deleteRange(r).setHorizontalRule().run() },
  { id: "date", label: "Today's date", hint: format(new Date(), "MMM d, yyyy"), icon: CalendarDays, keywords: "now", run: (e, r) => e.chain().focus().deleteRange(r).insertContent(`${format(new Date(), "MMM d, yyyy")} `).run() },
];

const SlashList = forwardRef<{ onKeyDown: (e: KeyboardEvent) => boolean }, SuggestionProps<SlashItem>>(function SlashList(props, ref) {
  const [index, setIndex] = useState(0);
  useEffect(() => setIndex(0), [props.items]);
  useImperativeHandle(ref, () => ({
    onKeyDown: (e: KeyboardEvent) => {
      if (!props.items.length) return false;
      if (e.key === "ArrowDown") {
        setIndex((i) => (i + 1) % props.items.length);
        return true;
      }
      if (e.key === "ArrowUp") {
        setIndex((i) => (i - 1 + props.items.length) % props.items.length);
        return true;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        const item = props.items[index];
        if (item) props.command(item);
        return true;
      }
      return false;
    },
  }));
  if (!props.items.length) return null;
  return (
    <div className="w-64 overflow-hidden rounded-lg border bg-popover p-1 text-popover-foreground shadow-md">
      {props.items.map((item, i) => (
        <button key={item.id} type="button" onMouseDown={(e) => (e.preventDefault(), props.command(item))} className={cn("flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left text-[13px]", i === index && "bg-accent")}>
          <span className="flex size-7 shrink-0 items-center justify-center rounded-md border border-border/70 bg-background text-muted-foreground">
            <item.icon className="size-4" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block truncate font-medium">{item.label}</span>
            <span className="block truncate text-[11.5px] text-muted-foreground">{item.hint}</span>
          </span>
        </button>
      ))}
    </div>
  );
});

const suggestion: Omit<SuggestionOptions<SlashItem>, "editor"> = {
  char: "/",
  startOfLine: false,
  allowSpaces: false,
  items: ({ query }) => {
    const q = query.toLowerCase();
    return ITEMS.filter((i) => !q || i.label.toLowerCase().includes(q) || i.keywords?.includes(q)).slice(0, 10);
  },
  command: ({ editor, range, props }) => props.run(editor, range),
  render: () => {
    let renderer: ReactRenderer<{ onKeyDown: (e: KeyboardEvent) => boolean }, SuggestionProps<SlashItem>> | null = null;
    let el: HTMLDivElement | null = null;
    const place = (props: SuggestionProps<SlashItem>) => {
      const r = props.clientRect?.();
      if (!r || !el) return;
      const top = r.bottom + 6;
      const flip = top + 300 > window.innerHeight;
      el.style.left = `${Math.min(r.left, window.innerWidth - 272)}px`;
      el.style.top = flip ? "" : `${top}px`;
      el.style.bottom = flip ? `${window.innerHeight - r.top + 6}px` : "";
    };
    return {
      onStart: (props) => {
        el = document.createElement("div");
        el.style.position = "fixed";
        el.style.zIndex = "60";
        document.body.appendChild(el);
        renderer = new ReactRenderer(SlashList, { props, editor: props.editor });
        el.appendChild(renderer.element);
        place(props);
      },
      onUpdate: (props) => {
        renderer?.updateProps(props);
        place(props);
      },
      onKeyDown: ({ event }) => {
        if (event.key === "Escape") return false;
        return renderer?.ref?.onKeyDown(event) ?? false;
      },
      onExit: () => {
        renderer?.destroy();
        el?.remove();
        renderer = null;
        el = null;
      },
    };
  },
};

export const SlashCommands = Extension.create({
  name: "slashCommands",
  addProseMirrorPlugins() {
    return [Suggestion({ editor: this.editor, pluginKey: new PluginKey("slashCommands"), ...suggestion })];
  },
});
